import React, {useMemo} from 'react'
import Tr from 'translations/Translations';
import SimpleTable from 'components/complex/simpleTable/SimpleTable';
import Loading from 'components/Loading';
import moment from 'moment';
import WorkableIconDeleteButton from 'components/button/WorkableIconDeleteButton';
import DateFormats from "constants/DateFormats";
import IconExternalSignButton from "components/button/IconExternalSignButton";
import makeStyles from "@material-ui/core/styles/makeStyles";
import PropTypes from 'prop-types';
import ContractorReportActionButton, {ACTION_DOWNLOAD, ACTION_HISTORY} from "components/complex/contractor/ContractorReportActionButton";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { useTheme } from '@material-ui/core/styles';
import { Tooltip } from '@material-ui/core';
import {connect} from 'react-redux';
import {KysStatus} from 'constants/KysStatus';

const useStyle = makeStyles(theme => ({
    root: {},
    cell: {
        padding: theme.spacing(1)
    },
    lpColumn: theme.components.table.lpColumn,
    dateColumn: theme.components.table.dateColumn,
    buttonColumn: theme.components.table.buttonColumn,
    nameColumn: {
        wordBreak: "break-all",
        paddingRight: theme.spacing(2),
    },
    nipColumn: {
        width: 150,
    },
    addressColumn: {
        width: 200,
        whiteSpace: "pre-line"
    },
    emailColumn: {
        width: 170,
        wordBreak: "break-all"
    },
    verificationColumn: {
        width: 170,
    },
    verificationActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    actionItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    icon: {
        fontSize: 30
    }
}));

function ContractorTable(
    {
        contractors,
        demandId,
        handleDelete,
        handleExternalSign,
        showJoinDate,
        demandHeader,
        onReportDownloaded,
        verification = false
    }) {

    const classes = useStyle();
    const showExternalSignColumn = !!handleExternalSign && contractors && contractors.items.findIndex((item, i) => item.externalOfferSign !== null) >= 0;
    const theme = useTheme();

    const createItem = (item, i) => {
        const {contractor, invitationDate, externalOfferSign, joinMethod, joinDate, kysStatus, editableKys} = item;
        const arr = [
            `${i + 1}`,
            contractor.name,
            `${contractor.vatin.prefix} ${contractor.vatin.number}`,
            `${contractor.address} ${contractor.postCode} ${contractor.city}`,
            contractor.email,
        ];

        if(showJoinDate)
            arr.push(joinMethod === "self" ? moment(joinDate).format(DateFormats.DATETIME_FORMAT) : '')

        arr.push(invitationDate ? moment(invitationDate).format(DateFormats.DATETIME_FORMAT) : '');
        arr.push(
            <div className={classes.verificationActions}>
                <div className={classes.actionItem}>
                    {kysStatus ?
                        <Tooltip
                            title={
                                kysStatus === KysStatus.VERIFIED ? Tr('contractor_report_status_verified') :
                                kysStatus === KysStatus.NEGATIVE ? Tr('contractor_report_status_negative_verification') :
                                Tr('contractor_report_status_not_verified')
                            }
                            arrow
                            placement="top"
                        >
                            <CheckCircleIcon
                                className={classes.icon}
                                style={{
                                    color: kysStatus === KysStatus.VERIFIED ? theme.palette.success.main :
                                        kysStatus === KysStatus.NEGATIVE ? theme.palette.error.main :
                                            theme.palette.warning.main
                                }}
                            />
                        </Tooltip> : <></>
                    }
                </div>
                <div className={classes.actionItem}>
                    <ContractorReportActionButton
                        action={ACTION_DOWNLOAD}
                        enabled={editableKys}
                        demandId={demandId}
                        contractorNip={contractor.vatin?.number}
                        contractorPostCode={contractor.postCode}
                        demandHeader={demandHeader}
                        onReportDownloaded={onReportDownloaded}
                        verification={verification}
                    />
                </div>
                <div className={classes.actionItem}>
                    <ContractorReportActionButton
                        action={ACTION_HISTORY}
                        demandId={demandId}
                        contractorNip={contractor.vatin?.number}
                        contractorPostCode={contractor.postCode}
                        demandHeader={demandHeader}
                        verification={verification}
                    />
                </div>
            </div>
        );
        if (handleDelete)
            arr.push(<WorkableIconDeleteButton onClick={() => handleDelete(item)}/>);
        if (showExternalSignColumn)
            arr.push(externalOfferSign !== null ? <IconExternalSignButton enabled={externalOfferSign} onClick={() => handleExternalSign(item)}/> : '');
        return arr;
    }

    const columnClasses = useMemo(() => {
        const arr = [
            classes.lpColumn,
            classes.nameColumn,
            classes.nipColumn,
            classes.addressColumn,
            classes.emailColumn,
        ];
        if(showJoinDate)
            arr.push(classes.dateColumn);

        arr.push(classes.dateColumn);
        arr.push(classes.verificationColumn);

        if (handleDelete)
            arr.push(classes.buttonColumn);

        if (showExternalSignColumn)
            arr.push(classes.buttonColumn)
        return arr;
    }, [showExternalSignColumn, handleDelete, showJoinDate, classes]);


    const headers = useMemo(() => {
        const arr = [
            Tr('column_lp'),
            Tr('name'),
            Tr('nip_pesel'),
            Tr('adres'),
            Tr('email')
        ]
        if(showJoinDate)
            arr.push(Tr('join_demand_date'));

        arr.push(Tr('forwarding_invitation_date'));
        arr.push(Tr('contractor_verification_actions'));

        if (handleDelete)
            arr.push('');

        if (showExternalSignColumn)
            arr.push('');
        return arr;
    }, [showExternalSignColumn, showJoinDate, handleDelete])

    if (!contractors) return <Loading/>
    return (
        <div className={classes.root}>
            <SimpleTable
                headers={headers}
                items={
                    contractors.items.map((item, i) => [
                        ...createItem(item, i)
                    ])
                }
                columnClasses={columnClasses}
                cellClassName={classes.cell}
            />
        </div>
    )
}

ContractorTable.propTypes = {
    contractors: PropTypes.object,
    demandId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    handleDelete: PropTypes.func,
    handleExternalSign: PropTypes.func,
    showJoinDate: PropTypes.bool,
    demandHeader: PropTypes.object,
    onReportDownloaded: PropTypes.func,
}

export default connect((state) => ({
    demandHeader: state.resources.demandHeader
}), {})(ContractorTable);
